import { formOptions } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Trash } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import ConfirmModal from '~/components/ConfirmModal';
import DataTable from '~/components/DataTable';
import TooltipButton from '~/partials/Tooltip';
import { Button } from '~/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '~/primitives/dialog';
import { ScrollArea } from '~/primitives/scroll-area';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { ModalProps } from '~/util';
import {
    levelUpdateFormInput,
    levelUpdateFormSchema,
} from '~/util/forms/level';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { useAppForm } from '~/util/hooks/useAppForm';
import { useList } from '~/util/hooks/useList';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import {
    createUpdateMutation,
    deleteUpdateMutation,
    editUpdateMutation,
} from '~/util/mutations/staff/levels';
import { levelUpdatesQuery } from '~/util/queries/public/levels';
import { LevelUpdate, levelUpdateTypes } from '~/util/types/levels';

const Route = getRouteApi('/list/$levelid');

export const LevelUpdates = () => {
    const list = useList();
    const { levelid } = Route.useParams();

    const localeDateString = useLocaleDateString();

    const [editModalOpen, setEditModalOpen] = useState(false);
    const data = useQuery(levelUpdatesQuery({ list, levelid }))?.data?.sort(
        (a, b) => {
            if (!a.timestamp) return -1;
            if (!b.timestamp) return 1;
            return (
                new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime()
            );
        },
    );

    const columns: ColumnDef<LevelUpdate>[] = useMemo(
        () => [
            {
                accessorKey: 'timestamp',
                header: 'Date',
                cell: ({
                    row: {
                        original: { timestamp },
                    },
                }) => (timestamp ? localeDateString(timestamp) : 'N/A'),
            },
            {
                accessorKey: 'update_type',
                header: 'Type',
                cell: ({
                    row: {
                        original: { update_type },
                    },
                }) => update_type,
            },
            {
                accessorKey: 'changelog',
                header: 'Changelog / Notes',
                cell: ({
                    row: {
                        original: { changelog },
                    },
                }) => changelog,
            },
        ],
        [localeDateString],
    );

    return (
        <div className="mt-6 mr-4 ml-2 text-left">
            <div className="flex mb-2">
                <h2 className="transition-colors text-3xl font-medium px-0.5 hover:text-secondary-light">
                    Level Updates
                </h2>
                <Pencil
                    className="size-6 ml-auto hover:text-secondary-light transition-colors cursor-pointer"
                    onClick={() => setEditModalOpen(true)}
                />
            </div>

            {data?.length === 0 ? (
                'This level does not have any updates.'
            ) : (
                <DataTable
                    columns={columns}
                    data={data || []}
                    headerClassName={() =>
                        'clamp-[text-xs-sm-clamp] px-0.5 lg:p-2 bg-transparent hover:!bg-transparent'
                    }
                    cellClassName={() => 'px-0.5 py-2 lg:px-2'}
                    rowClassName={() => 'bg-transparent hover:!bg-transparent'}
                />
            )}

            <EditLevelUpdatesDialog
                open={editModalOpen}
                onOpenChange={setEditModalOpen}
            />
        </div>
    );
};

export const EditLevelUpdatesDialog = (props: ModalProps) => {
    const [selectedUpdate, setSelectedUpdate] = useState<LevelUpdate | null>(
        null,
    );
    const [isNewSheetOpen, setIsNewSheetOpen] = useState(false);
    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const list = useList();

    const { levelid } = Route.useParams();

    const { data, isPending } = useQuery(levelUpdatesQuery({ list, levelid }));

    const localeDateString = useLocaleDateString();
    const deleteMutation = useApiMutation(deleteUpdateMutation());

    const columns: ColumnDef<LevelUpdate>[] = useMemo(
        () => [
            {
                accessorKey: 'update_type',
                header: 'Type',
                cell: ({
                    row: {
                        original: { update_type },
                    },
                }) => update_type,
            },
            {
                accessorKey: 'changelog',
                header: 'Changelog / Notes',
                cell: ({
                    row: {
                        original: { changelog },
                    },
                }) => changelog,
            },
            {
                accessorKey: 'timestamp',
                header: 'Date',
                cell: ({
                    row: {
                        original: { timestamp },
                    },
                }) => localeDateString(timestamp),
            },
            {
                id: 'actions',
                cell: ({ row: { original: update } }) => {
                    return (
                        <div className="flex items-center gap-1">
                            <TooltipButton text="Edit Update" side="top">
                                <Button
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => {
                                        setSelectedUpdate(update);
                                        setIsEditSheetOpen(true);
                                        e.stopPropagation();
                                    }}
                                >
                                    <Pencil className="h-5 w-5 m-auto" />
                                </Button>
                            </TooltipButton>
                            <TooltipButton text="Delete Update" side="top">
                                <Button
                                    variant="destructive"
                                    className="h-8 w-8 p-0"
                                    onClick={(event) => {
                                        setSelectedUpdate(update);
                                        setIsConfirmModalOpen(true);
                                        event.stopPropagation();
                                    }}
                                >
                                    <Trash className="h-5 w-5 m-auto" />
                                </Button>
                            </TooltipButton>
                        </div>
                    );
                },
            },
        ],
        [localeDateString],
    );

    return (
        <Dialog {...props}>
            <DialogContent disableDialogScroll>
                <DialogHeader className="pl-3">
                    <DialogTitle>Updates</DialogTitle>
                    <DialogDescription>
                        Updates that this level has received. These are
                        displayed on the level&apos;s records page, separating
                        records from before/after the update, based on the
                        update&apos;s given date. The changelog text field can
                        be used to specify important things that were changed,
                        details about what type of update it is if it
                        doesn&apos;t entirely fit one of the pre-existing types,
                        etc..
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="min-h-0 flex-1 mr-1 pr-3">
                    <Button
                        className="my-4"
                        onClick={() => setIsNewSheetOpen(true)}
                    >
                        <Plus /> Add Update
                    </Button>
                    <DataTable
                        isLoading={isPending}
                        columns={columns}
                        data={data ?? []}
                    />
                </ScrollArea>
                <ConfirmModal
                    open={isConfirmModalOpen}
                    onOpenChange={setIsConfirmModalOpen}
                    title="Delete Update"
                    text="Are you sure you want to delete this update? This action cannot be undone."
                    onConfirm={() => {
                        if (!selectedUpdate) return;
                        deleteMutation.mutate({
                            update_id: selectedUpdate.id,
                            levelid,
                            list,
                        });
                    }}
                />
                <NewUpdateSheet
                    open={isNewSheetOpen}
                    onOpenChange={setIsNewSheetOpen}
                />
                <EditUpdateSheet
                    update={selectedUpdate!}
                    open={isEditSheetOpen}
                    onOpenChange={setIsEditSheetOpen}
                />
            </DialogContent>
        </Dialog>
    );
};

export const NewUpdateSheet: React.FC<ModalProps> = (modalProps) => {
    const list = useList();

    const { levelid } = Route.useParams();

    const mutation = useApiMutation({
        ...createUpdateMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'add-update-staff',
            defaultValues: {} as levelUpdateFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelUpdateFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({ data: value, list, levelid });
            },
        }),
    );

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Add Update</SheetTitle>
                        <SheetDescription>
                            Add a new update to this level. This will be
                            displayed on the level&apos;s records page.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="add-update-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="update_type">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelUpdateTypes}
                                        label="Update Type"
                                        required
                                        placeholder="Select Update Type"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="timestamp">
                                {(field) => (
                                    <field.DateTimeField
                                        label="Timestamp"
                                        labelNotes="The time at which this update was released. This will be used to separate records before/after it."
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="changelog">
                                {(field) => (
                                    <field.TextAreaField
                                        label="Changelog / Notes"
                                        placeholder="Additional information about this update, what it changed, etc.."
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Add Update"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};

export const EditUpdateSheet: React.FC<
    { update: LevelUpdate } & ModalProps
> = ({ update, ...modalProps }) => {
    const list = useList();

    const { levelid } = Route.useParams();

    const mutation = useApiMutation({
        ...editUpdateMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'edit-update-staff',
            defaultValues: update as levelUpdateFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelUpdateFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({
                    data: value,
                    list,
                    levelid,
                    update_id: update.id,
                });
            },
        }),
    );

    useEffect(() => {
        form.reset(update);
    }, [update, form]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit Update</SheetTitle>
                        <SheetDescription>
                            Update this update&apos;s content and timestamp.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="edit-update-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="update_type">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelUpdateTypes}
                                        label="Update Type"
                                        required
                                        placeholder="Select Update Type"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="timestamp">
                                {(field) => (
                                    <field.DateTimeField
                                        label="Timestamp"
                                        labelNotes="The time at which this update was released. This will be used to separate records before/after it."
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="changelog">
                                {(field) => (
                                    <field.TextAreaField
                                        label="Changelog / Notes"
                                        placeholder="Additional information about this update, what it changed, etc.."
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Edit Update"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};
